/**
 * <knack-accordion> - progressive enhancement for the Knack PDP accordion. ORIGINAL Horizon code.
 *
 * The rows are native <details>/<summary> (see blocks/knack-accordion-row.liquid), so the accordion
 * is fully keyboard- and screen-reader-operable and works with NO JavaScript (rows just toggle
 * instantly, as the browser does natively). This element only ADDS a smooth open/close height
 * animation on top, so an expanding panel eases open instead of snapping (the "jumpy" feel).
 *
 * Reduced motion: when the user prefers reduced motion we do nothing and let the native instant
 * toggle stand. If the Web Animations API is missing we also fall back to native.
 */
class KnackAccordion extends HTMLElement {
  #reduce = null;

  connectedCallback() {
    this.#reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.querySelectorAll('details.knack-accordion__row').forEach((row) => this.#wire(row));
  }

  #wire(row) {
    const summary = row.querySelector('summary');
    const content = row.querySelector('.knack-accordion__content');
    if (!summary || !content) return;

    summary.addEventListener('click', (event) => {
      // Native path: reduced motion, no animation support, or an in-flight animation on this row.
      if (this.#reduce.matches || typeof content.animate !== 'function') return;
      if (row.dataset.knackAnimating) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      if (row.open) {
        this.#collapse(row, content);
      } else {
        this.#expand(row, content);
      }
    });
  }

  #expand(row, content) {
    row.dataset.knackAnimating = 'open';
    row.open = true; // reveal so we can measure the natural height, then grow into it
    const target = content.scrollHeight;
    const animation = content.animate(
      [
        { height: '0px', opacity: 0 },
        { height: `${target}px`, opacity: 1 },
      ],
      { duration: 260, easing: 'ease' }
    );
    animation.onfinish = animation.oncancel = () => {
      delete row.dataset.knackAnimating;
    };
  }

  #collapse(row, content) {
    row.dataset.knackAnimating = 'close';
    const start = content.scrollHeight;
    const animation = content.animate(
      [
        { height: `${start}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 220, easing: 'ease' }
    );
    animation.onfinish = animation.oncancel = () => {
      row.open = false; // hide only once the panel has finished shrinking
      delete row.dataset.knackAnimating;
    };
  }
}

if (!customElements.get('knack-accordion')) {
  customElements.define('knack-accordion', KnackAccordion);
}
